import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Plus, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

import ImageCropperModal from './ImageCropperModal';
import { Crop } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RidersTab({ riders, onRefresh, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingRider, setEditingRider] = useState(null);
  const [formData, setFormData] = useState({ name: '', tuner: '', nationality: '', price: 15000, image_url: '', description: '', achievements: '', stats: '' });
  const [uploading, setUploading] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      setUploading(true);
      toast.loading('Uploading image...');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      
      const imageUrl = response.data.url;
      setCropperImage(imageUrl);
      toast.success('Image uploaded! Crop it now or close to use as-is.');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRider) {
        await axios.put(`${BACKEND_URL}/api/moto/riders/${editingRider.rider_id}`, formData, { withCredentials: true });
        toast.success('Rider updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/moto/riders`, formData, { withCredentials: true });
        toast.success('Rider created');
      }
      setIsOpen(false);
      setEditingRider(null);
      setFormData({ name: '', tuner: '', nationality: '', price: 15000, image_url: '', description: '', achievements: '', stats: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to save rider');
    }
  };

  const openEdit = (rider) => {
    setEditingRider(rider);
    setFormData({ name: rider.name, tuner: rider.tuner, nationality: rider.nationality || '', price: rider.price || 15000, image_url: rider.image_url || '', description: rider.description || '', achievements: rider.achievements || '', stats: rider.stats || '' });
    setIsOpen(true);
  };

  return (
    <>
    <div className="bg-card border border-zinc-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-2xl">Moto <span className="text-secondary">Riders</span> Management</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-rider-button"
              onClick={() => { setEditingRider(null); setFormData({ name: '', tuner: '', nationality: '', price: 15000, image_url: '', description: '', achievements: '', stats: '' }); }}
              className="btn-skew bg-secondary text-black px-6 py-2 font-bold uppercase flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Rider</span>
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border border-zinc-800 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl">
                {editingRider ? 'Edit Rider' : 'Add Rider'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Nationality</label>
                  <input type="text" value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Tuner/Team</label>
                <input type="text" value={formData.tuner} onChange={(e) => setFormData({ ...formData, tuner: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Price (Credits: 5K-50K)</label>
                <input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Image</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input type="text" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="Enter URL or upload file" className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" />
                    <label className={`flex items-center gap-2 px-4 h-12 cursor-pointer transition-colors ${uploading ? 'bg-zinc-700' : 'bg-secondary hover:bg-secondary/80'} text-black`}>
                      <Upload size={18} />
                      <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                  </div>
                  {formData.image_url && (
                    <div className="flex items-center gap-2">
                      <img src={formData.image_url} alt="Preview" className="w-16 h-16 object-cover border border-zinc-700" />
                      <button type="button" onClick={() => setCropperImage(formData.image_url)} className="flex items-center gap-1 text-primary hover:text-primary/80 text-sm"><Crop size={14} /> Crop</button>
                      <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })} className="text-red-500 hover:text-red-400 text-sm">Remove</button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Description (optional)</label>
                <textarea data-testid="rider-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary px-4 py-3 text-white min-h-[80px]" placeholder="Brief bio about the rider..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Achievements (optional)</label>
                <textarea data-testid="rider-achievements-input" value={formData.achievements} onChange={(e) => setFormData({ ...formData, achievements: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary px-4 py-3 text-white min-h-[80px]" placeholder="Championships, wins, records..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Stats (optional)</label>
                <textarea data-testid="rider-stats-input" value={formData.stats} onChange={(e) => setFormData({ ...formData, stats: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary px-4 py-3 text-white min-h-[80px]" placeholder="Career stats, lap times..." />
              </div>
              <button type="submit" className="w-full bg-secondary text-black py-3 font-bold uppercase">
                {editingRider ? 'Update' : 'Create'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">NAME</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">TUNER</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">PRICE</th>
              <th className="text-right p-4 font-heading font-bold text-sm text-zinc-400">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {riders.map((rider) => (
              <tr key={rider.rider_id} className="border-b border-zinc-800 hover:bg-zinc-900">
                <td className="p-4 font-bold">{rider.name}</td>
                <td className="p-4 text-zinc-400">{rider.tuner}</td>
                <td className="p-4 text-secondary font-mono">{(rider.price || 0).toLocaleString()} CR</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(rider)} className="text-secondary hover:text-white mr-3">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => onDelete(rider.rider_id)} className="text-red-500 hover:text-red-400">
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

      {cropperImage && (
        <ImageCropperModal
          imageSrc={cropperImage}
          onCropped={async (url) => {
            setFormData(prev => ({ ...prev, image_url: url }));
            setCropperImage(null);
            if (editingRider) {
              try {
                await axios.put(`${BACKEND_URL}/api/moto/riders/${editingRider.rider_id}`, { ...formData, image_url: url }, { withCredentials: true });
                toast.success('Rider photo updated!');
                onRefresh();
              } catch { toast.error('Photo cropped but failed to save rider'); }
            }
          }}
          onClose={() => { setFormData(prev => prev.image_url ? prev : { ...prev, image_url: cropperImage }); setCropperImage(null); }}
        />
      )}
    </>
  );
}